import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Boxes,
  Building,
  Building2,
  CheckCircle,
  FileText,
  FolderKanban,
  Handshake,
  Layers,
  MapPin,
  Shield,
  Timer,
  TrendingUp,
  Users,
  Workflow,
  XCircle,
} from 'lucide-react';
import { motion } from 'motion/react';

import businessContracts from '@src/assets/screenshots/business-contracts.webp';
import businessEntities from '@src/assets/screenshots/business-entities.webp';
import businessObjectives from '@src/assets/screenshots/business-objectives.webp';
import businessProcesses from '@src/assets/screenshots/business-processes.webp';
import businessServices from '@src/assets/screenshots/business-services.webp';
import businessVendors from '@src/assets/screenshots/business-vendors.webp';
import financeSpendDashboard from '@src/assets/screenshots/finance-spend-dashboard.webp';
import { AIAgentBanner } from '@src/components/AIAgentBanner';
import { ScreenshotGallery } from '@src/components/ScreenshotGallery';
import AnswerSection from '@src/components/seo/AnswerSection';
import { Button } from '@src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

const BOSSS_BLUE = '#2973a3';
const PURPLE = '#8b5cf6';
const BG_COLOR = '#F8FAFC';

// Pain points for Business Management
const painPoints = [
  {
    icon: XCircle,
    problem: 'No Business Context',
    description: 'IT assets exist in a vacuum—no connection to the business units, processes, or people they support.',
    solution: 'Comprehensive entity management links assets to organizations, departments, and business processes.',
  },
  {
    icon: Timer,
    problem: 'Vendor Sprawl',
    description: 'Third-party relationships scattered across contracts, spreadsheets, and email with no central visibility.',
    solution: 'Unified vendor registry with contract tracking, risk assessment, and relationship management.',
  },
  {
    icon: AlertTriangle,
    problem: 'Process Blindness',
    description: 'Business processes undocumented, making it impossible to assess impact of IT changes or outages.',
    solution: 'Business process catalog with asset dependencies, owner assignments, and criticality ratings.',
  },
  {
    icon: Layers,
    problem: 'Compliance Gaps',
    description: 'No visibility into regulatory requirements across different business entities and jurisdictions.',
    solution: 'Multi-entity compliance tracking with industry-specific regulations and maturity assessments.',
  },
];

// Entity types - 17 categories
const entityCategories = [
  { name: 'Entities', icon: Building, desc: 'Organizations & business units', color: '#3b82f6' },
  { name: 'Processes', icon: Workflow, desc: 'Business process documentation', color: '#10b981' },
  { name: 'Departments', icon: Users, desc: 'Organizational structure', color: '#8b5cf6' },
  { name: 'Locations', icon: MapPin, desc: 'Physical sites & facilities', color: '#f59e0b' },
  { name: 'Vendors', icon: Handshake, desc: 'Third-party relationships', color: '#ef4444' },
  { name: 'Contracts', icon: FileText, desc: 'Agreement management', color: '#06b6d4' },
  { name: 'Products', icon: Boxes, desc: 'Product & service catalog', color: '#84cc16' },
  { name: 'Projects', icon: FolderKanban, desc: 'Project portfolio tracking', color: '#ec4899' },
];

// Maturity levels
const maturityLevels = [
  { level: 'Initial', count: 2, width: '20%', color: '#ef4444' },
  { level: 'Developing', count: 5, width: '40%', color: '#f59e0b' },
  { level: 'Defined', count: 8, width: '60%', color: '#3b82f6' },
  { level: 'Managed', count: 4, width: '80%', color: '#10b981' },
  { level: 'Optimized', count: 1, width: '100%', color: '#8b5cf6' },
];

// Dashboard metrics
const dashboardMetrics = [
  { label: 'Entities', value: '12', color: PURPLE },
  { label: 'Vendors', value: '45', color: '#f59e0b' },
  { label: 'Processes', value: '28', color: '#10b981' },
  { label: 'Contracts', value: '67', color: '#3b82f6' },
];

// Capabilities
const capabilities = [
  {
    icon: Building,
    title: 'Entity Management',
    description: 'Track internal and external organizations with industry classification and regulatory requirements.',
    features: ['NAICS codes', 'Industry tagging', 'Regulatory mapping', 'Maturity scoring'],
  },
  {
    icon: Handshake,
    title: 'Vendor Management',
    description: 'Centralize third-party relationships with risk assessment, contracts, and performance tracking.',
    features: ['Vendor registry', 'Risk scoring', 'Contract tracking', 'SLA monitoring'],
  },
  {
    icon: Workflow,
    title: 'Process Mapping',
    description: 'Document business processes with asset dependencies, owners, and criticality ratings.',
    features: ['Process catalog', 'Dependency mapping', 'Impact analysis', 'Owner assignment'],
  },
  {
    icon: Shield,
    title: 'Compliance Tracking',
    description: 'Monitor regulatory compliance across entities with multi-framework support.',
    features: ['Multi-framework', 'Gap analysis', 'Evidence collection', 'Audit readiness'],
  },
];

// Sample entities for preview
const sampleEntities = [
  {
    name: 'SecureOne Labs',
    type: 'Internal',
    industry: 'Information Technology',
    naics: '541512',
    maturity: 'Advanced',
    maturityColor: '#10b981',
    regulated: true,
    size: 'Medium',
  },
  {
    name: 'Acme Healthcare Corp',
    type: 'Customer',
    industry: 'Healthcare',
    naics: '622110',
    maturity: 'Intermediate',
    maturityColor: '#f59e0b',
    regulated: true,
    size: 'Large',
  },
  {
    name: 'CloudTech Solutions',
    type: 'Vendor',
    industry: 'Cloud Services',
    naics: '518210',
    maturity: 'Advanced',
    maturityColor: '#10b981',
    regulated: false,
    size: 'Small',
  },
];

export function BusinessPage() {
  const onNavigateToTop = useNavigateToTop();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28" style={{ background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 50%, #d8b4fe 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-20 left-20"
            animate={{ rotate: [0, 10, -10, 0], y: [0, -15, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Building className="w-8 h-8" style={{ color: `${PURPLE}50` }} />
          </motion.div>

          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            className="absolute bottom-40 right-20"
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Workflow className="w-6 h-6" style={{ color: `${PURPLE}40` }} />
          </motion.div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
            >
              <div
                style={{ backgroundColor: `${PURPLE}15`, color: PURPLE }}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
              >
                <Building className="w-4 h-4 mr-2" />
                Business Context
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Connect IT to Business Value
              </h1>

              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                BOSSS Business provides the organizational context that transforms raw IT data
                into business intelligence—linking assets to entities, vendors, processes, and people.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {dashboardMetrics.map((metric, index) => (
                  <motion.div
                    key={index}
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="text-center p-3 bg-white/80 rounded-lg shadow-sm"
                  >
                    <div className="text-2xl font-bold" style={{ color: metric.color }}>{metric.value}</div>
                    <div className="text-xs text-gray-600">{metric.label}</div>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    style={{ backgroundColor: PURPLE }}
                    className="text-white px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl"
                    onClick={() => onNavigateToTop('/demo')}
                  >
                    Schedule a Demo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>

                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    variant="outline"
                    style={{ borderColor: PURPLE, color: PURPLE }}
                    className="px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg"
                    onClick={() => onNavigateToTop('/contact')}
                  >
                    Contact Sales
                  </Button>
                </motion.div>
              </div>
            </motion.div>

            {/* Dashboard Preview */}
            <motion.div
              className="relative"
              animate={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white rounded-xl shadow-2xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div style={{ backgroundColor: `${PURPLE}15` }} className="w-10 h-10 rounded-lg flex items-center justify-center">
                      <Building className="w-5 h-5" style={{ color: PURPLE }} />
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900">Business Entities</h3>
                      <p className="text-xs text-gray-500">Internal & External Organizations</p>
                    </div>
                  </div>
                </div>

                {/* Sample Entity Cards */}
                <div className="space-y-3">
                  {sampleEntities.map((entity, i) => (
                    <motion.div
                      key={i}
                      animate={{ opacity: 1, x: 0 }}
                      initial={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.3, delay: 0.5 + i * 0.1 }}
                      className="p-3 rounded-lg border border-gray-100 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900 text-sm">{entity.name}</h4>
                          <p className="text-xs text-gray-500">{entity.industry}</p>
                        </div>

                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            entity.type === 'Internal'
                              ? 'bg-blue-100 text-blue-700' :
                              entity.type === 'Customer'
                                ? 'bg-green-100 text-green-700' :
                                'bg-purple-100 text-purple-700'
                          }`}
                        >
                          {entity.type}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-xs">
                        <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                          NAICS:
                          {entity.naics}
                        </span>

                        <span className="px-2 py-0.5 rounded" style={{ backgroundColor: `${entity.maturityColor}15`, color: entity.maturityColor }}>
                          {entity.maturity}
                        </span>

                        {entity.regulated && (
                          <span className="px-2 py-0.5 rounded bg-red-100 text-red-600">Regulated</span>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path="/platform/business" />


      {/* Pain Points Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              IT Without Business Context Is Noise
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Raw IT data means nothing without understanding the business it supports.
              Here&apos;s how BOSSS Business connects the dots.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-red-100">
                        <point.icon className="w-6 h-6 text-red-600" />
                      </div>

                      <div>
                        <CardTitle className="text-xl text-gray-900 mb-2">
                          {point.problem}
                        </CardTitle>

                        <p className="text-sm text-gray-500">{point.description}</p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div style={{ backgroundColor: `${PURPLE}08` }} className="flex items-start gap-3 p-4 rounded-lg">
                      <CheckCircle style={{ color: PURPLE }} className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700">{point.solution}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Entity Categories Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              17 Business Categories
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive business context modeling for organizations of any size and complexity.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {entityCategories.map((category, i) => (
              <motion.div
                key={i}
                viewport={{ once: true }}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-lg transition-all group">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div style={{ backgroundColor: `${category.color}15` }} className="w-10 h-10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <category.icon className="w-5 h-5" style={{ color: category.color }} />
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm group-hover:text-purple-600 transition-colors">{category.name}</h4>
                        <p className="text-xs text-gray-500">{category.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Maturity Assessment Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <div
                style={{ backgroundColor: `${PURPLE}15`, color: PURPLE }}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-4"
              >
                <TrendingUp className="w-3 h-3 mr-1" />
                Maturity Assessment
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Track Organizational Maturity
              </h2>

              <p className="text-lg text-gray-600 mb-6">
                Assess and track the maturity of business entities across security, compliance,
                and operational dimensions. Identify gaps and prioritize improvements.
              </p>

              <ul className="space-y-3 text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: PURPLE }} />
                  5-level maturity model (Initial to Optimized)
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: PURPLE }} />
                  Industry-specific benchmarking
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: PURPLE }} />
                  Gap analysis and recommendations
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: PURPLE }} />
                  Progress tracking over time
                </li>
              </ul>
            </div>

            {/* Maturity Chart Preview */}
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <BarChart3 className="w-5 h-5" style={{ color: PURPLE }} />
                  <h4 className="font-semibold text-gray-900">Entity Maturity Distribution</h4>
                </div>

                <div className="space-y-4">
                  {maturityLevels.map((level, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 w-20">{level.level}</span>

                      <div className="flex-1 bg-gray-100 rounded-full h-4">
                        <motion.div
                          initial={{ width: '0%' }}
                          viewport={{ once: true }}
                          className="h-4 rounded-full"
                          whileInView={{ width: level.width }}
                          style={{ backgroundColor: level.color }}
                          transition={{ duration: 0.8, delay: 0.3 + i * 0.1 }}
                        />
                      </div>

                      <span className="text-xs font-medium text-gray-900 w-6">{level.count}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Complete Business Context
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to connect IT operations to business value and organizational context.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 group">
                  <CardHeader className="pb-2">
                    <div
                      style={{ backgroundColor: `${PURPLE}15` }}
                      className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    >
                      <capability.icon className="w-6 h-6" style={{ color: PURPLE }} />
                    </div>

                    <CardTitle className="text-lg group-hover:text-purple-600 transition-colors">
                      {capability.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">{capability.description}</p>

                    <ul className="space-y-1">
                      {capability.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-xs text-gray-500">
                          <CheckCircle className="w-3 h-3 mr-2" style={{ color: PURPLE }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* See It In Action Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Manage business entities, processes, vendors, and contracts with complete visibility
              into organizational context and dependencies.
            </p>
          </motion.div>

          <ScreenshotGallery
            accentColor={PURPLE}
            urlPath="business/processes"
            screenshots={[
              { src: businessProcesses, label: 'Processes', alt: 'BOSSS business processes mapped to the services and assets behind them' },
              { src: businessEntities, label: 'Entities', alt: 'BOSSS multi-entity hierarchy that scopes every other module' },
              { src: businessServices, label: 'Services', alt: 'BOSSS business service catalog with ownership and dependencies' },
              { src: businessObjectives, label: 'Objectives', alt: 'BOSSS business objectives linked to the processes that deliver them' },
              { src: businessContracts, label: 'Contracts', alt: 'BOSSS business contract management view' },
              { src: businessVendors, label: 'Vendors', alt: 'BOSSS vendor register with third-party risk context' },
              { src: financeSpendDashboard, label: 'Financials', alt: 'BOSSS spend dashboard consolidating revenue, profit, OPEX and CAPEX across every entity, with P&L, cash flow and balance sheet' },
            ]}
          />
        </div>
      </section>

      {/* AI Agent Banner */}
      <AIAgentBanner
        toolsCount={17}
        accentColor="#6366f1"
        agentName="Business Agent"
        description="Your AI-powered business operations copilot. Ask about entities, contracts, vendors, or processes—get instant answers."
        exampleQueries={[
          '"Which contracts expire in the next 90 days?"',
          '"Show me all vendors with high risk ratings"',
          '"List business processes for the Finance department"',
          '"What entities are in the healthcare industry?"',
        ]}
      />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-500 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Add Business Context?
            </h2>

            <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              See how BOSSS Business can transform your IT data into actionable business
              intelligence with full organizational context.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  className="bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                  onClick={() => onNavigateToTop('/demo')}
                >
                  Schedule a Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>

              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  variant="outline"
                  className="px-8 py-4 text-lg rounded-lg transition-all duration-200 border-white/80 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm"
                  onClick={() => onNavigateToTop('/contact')}
                >
                  Talk to Sales
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Building2, value: '500+', label: 'Entities Managed' },
              { icon: Handshake, value: '2K+', label: 'Vendor Relationships' },
              { icon: Workflow, value: '17', label: 'Business Categories' },
              { icon: Shield, value: '100%', label: 'Compliance Visibility' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <stat.icon style={{ color: BOSSS_BLUE }} className="w-8 h-8 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default BusinessPage;
